import * as FileSaver from "file-saver";

const exportToHTML = (data, options) => {
  const { fileName, truckInfo, dates, apiKey, company, logo } = options;

  const htmlTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Vehicle Tracking - Tracking Map</title>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap" rel="stylesheet">
    <style>
        :root {
            --primary-color: ${company === "greenhaul" ? "#3cba06" : "#0283ed"};
            --bg-color: #f8fafc;
            --card-bg: rgba(255, 255, 255, 0.95);
        }
        
        body, html {
            margin: 0;
            padding: 0;
            height: 100%;
            font-family: 'Outfit', sans-serif;
            background-color: var(--bg-color);
            overflow: hidden;
        }

        #map {
            width: 100%;
            height: 100vh;
        }

        .header-bar {
            position: absolute;
            top: 20px;
            left: 20px;
            right: 20px;
            background: var(--card-bg);
            backdrop-filter: blur(10px);
            padding: 12px 24px;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            display: flex;
            justify-content: space-between;
            align-items: center;
            z-index: 1000;
            border: 1px solid rgba(255,255,255,0.3);
        }

        .company-info {
            display: flex;
            align-items: center;
            gap: 15px;
        }

        .company-logo {
            height: 40px;
            object-fit: contain;
        }

        .company-name {
            font-weight: 900;
            font-size: 1.2rem;
            color: #1e293b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .vehicle-info {
            text-align: right;
        }

        .plate-number {
            font-weight: 900;
            font-size: 1.1rem;
            color: var(--primary-color);
            display: block;
        }

        .date-range {
            font-size: 0.8rem;
            color: #64748b;
            font-weight: 600;
        }

        /* Control Center Modal */
        .control-panel {
            position: absolute;
            bottom: 30px;
            left: 50%;
            transform: translateX(-50%);
            width: 95%;
            max-width: 1200px;
            background: var(--card-bg);
            backdrop-filter: blur(15px);
            border-radius: 24px;
            padding: 24px 32px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.15);
            z-index: 1000;
            border: 1px solid rgba(255,255,255,0.4);
        }

        .panel-header {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 20px;
            color: #475569;
        }

        .panel-header svg {
            width: 20px;
            height: 20px;
        }

        .panel-header span {
            font-weight: 800;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        .playback-display {
            font-size: 2.5rem;
            font-weight: 900;
            color: #1e293b;
            margin-bottom: 25px;
            line-height: 1;
        }

        .slider-container {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 25px;
        }

        input[type=range] {
            flex-grow: 1;
            accent-color: var(--primary-color);
            cursor: pointer;
        }

        .time-limit {
            font-size: 0.8rem;
            font-weight: 700;
            color: #94a3b8;
            min-width: 45px;
        }

        .controls-row {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 20px;
        }

        .speed-selector {
            background: #f1f5f9;
            border: none;
            padding: 8px 16px;
            border-radius: 10px;
            font-weight: 700;
            color: #475569;
            font-family: inherit;
            cursor: pointer;
            outline: none;
        }

        .btn-play {
            background: var(--primary-color);
            color: white;
            border: none;
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            transition: all 0.2s;
        }

        .btn-play:hover {
            transform: scale(1.1);
            filter: brightness(1.1);
        }

        .btn-reset {
            background: transparent;
            border: 1.5px solid #cbd5e1;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #64748b;
            transition: all 0.2s;
        }

        .btn-reset:hover {
            background: #f1f5f9;
            border-color: #94a3b8;
        }

        /* Traffic Button Overlay */
        .traffic-btn {
            position: absolute;
            bottom: 30px;
            left: 30px;
            background: white;
            border: none;
            padding: 10px 18px;
            border-radius: 12px;
            font-weight: 800;
            font-size: 0.85rem;
            color: #334155;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            cursor: pointer;
            transition: all 0.2s;
            z-index: 1000;
        }

        .traffic-btn.active {
            background: #22c55e;
            color: white;
        }
    </style>
</head>
<body>
    <div class="header-bar">
        <div class="company-info">
            <div class="company-name">${company === "greenhaul" ? "Green Haul Logistic GPS" : "Mewahtrans GPS"}</div>
        </div>
        <div class="vehicle-info">
            <span class="plate-number">${truckInfo}</span>
            <span class="date-range">${dates}</span>
        </div>
    </div>

    <button id="traffic-toggle" class="traffic-btn">Show Traffic</button>

    <div id="map"></div>

    <div class="control-panel">
        <div class="panel-header">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            <span>Vehicle Control Center</span>
        </div>
        
        <div id="time-display" class="playback-display">--:-- --</div>

        <div class="slider-container">
            <span class="time-limit" id="start-time-limit">00:00</span>
            <input type="range" id="time-slider" min="0" max="100" value="100">
            <span class="time-limit" id="end-time-limit">23:59</span>
        </div>

        <div class="controls-row">
            <select id="speed-multiplier" class="speed-selector">
                <option value="0.1">0.1x</option>
                <option value="0.2" selected>0.2x (Default)</option>
                <option value="0.5">0.5x</option>
                <option value="1">1x (Normal)</option>
                <option value="5">5x</option>
            </select>

            <button id="reset-btn" class="btn-reset" title="Reset">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            </button>

            <button id="play-pause-btn" class="btn-play">
                <svg id="play-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                <svg id="pause-icon" style="display:none" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            </button>
        </div>
    </div>

    <!-- Data Injection -->
    <script>
        const ROUTE_DATA = ${JSON.stringify(data)};
        const TRUCK_ICON_URL = "http://maps.google.com/mapfiles/ms/icons/truck.png"; // Fallback URL
    </script>

    <script src="https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry"></script>
    
    <script>
        let map, polyline, truckMarker, trafficLayer;
        let isPlaying = false;
        let playInterval;
        let currentIdx = ROUTE_DATA.length - 1;
        let currentIdxFloat = ROUTE_DATA.length - 1;

        const slider = document.getElementById('time-slider');
        const timeDisplay = document.getElementById('time-display');
        const playBtn = document.getElementById('play-pause-btn');
        const playIcon = document.getElementById('play-icon');
        const pauseIcon = document.getElementById('pause-icon');
        const resetBtn = document.getElementById('reset-btn');
        const speedSelector = document.getElementById('speed-multiplier');
        const trafficToggle = document.getElementById('traffic-toggle');
        const startTimeLimit = document.getElementById('start-time-limit');
        const endTimeLimit = document.getElementById('end-time-limit');

        function formatLabel(idx) {
           const point = ROUTE_DATA[idx];
           if(!point) return "--:-- --";
           const ts = point.timestamp ? parseInt(point.timestamp) * 1000 : new Date(point.time_interval).getTime();
           return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }

        function initMap() {
            if (ROUTE_DATA.length === 0) return;

            const startPos = { lat: parseFloat(ROUTE_DATA[0].lat), lng: parseFloat(ROUTE_DATA[0].lng) };
            const endPos = { lat: parseFloat(ROUTE_DATA[ROUTE_DATA.length-1].lat), lng: parseFloat(ROUTE_DATA[ROUTE_DATA.length-1].lng) };
            
            map = new google.maps.Map(document.getElementById('map'), {
                zoom: 15,
                center: endPos,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false
            });

            trafficLayer = new google.maps.TrafficLayer();

            polyline = new google.maps.Polyline({
                path: ROUTE_DATA.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) })),
                geodesic: true,
                strokeColor: '#3b82f6',
                strokeOpacity: 0.8,
                strokeWeight: 3,
                map: map,
                icons: [{
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 2.5,
                        strokeColor: '#1e3a8a',
                        fillColor: '#1e3a8a',
                        fillOpacity: 1
                    },
                    offset: '100%',
                    repeat: '40px'
                }]
            });

            truckMarker = new google.maps.Marker({
                position: endPos,
                map: map,
                icon: {
                    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 6,
                    fillColor: '#3b82f6',
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: '#ffffff',
                    rotation: parseFloat(ROUTE_DATA[ROUTE_DATA.length-1].direction || 0)
                }
            });

            // Adjust bounds
            const bounds = new google.maps.LatLngBounds();
            ROUTE_DATA.forEach(p => bounds.extend({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }));
            map.fitBounds(bounds);

            // Initialize slider and UI
            slider.min = 0;
            slider.max = ROUTE_DATA.length - 1;
            slider.value = ROUTE_DATA.length - 1;
            
            startTimeLimit.innerText = formatLabel(0);
            endTimeLimit.innerText = formatLabel(ROUTE_DATA.length - 1);
            
            updateUI(ROUTE_DATA.length - 1);
        }

        function updateUI(idx) {
            const point = ROUTE_DATA[idx];
            if (!point) return;

            timeDisplay.innerText = formatLabel(idx);

            const pos = { lat: parseFloat(point.lat), lng: parseFloat(point.lng) };
            truckMarker.setPosition(pos);
            
            const icon = truckMarker.getIcon();
            icon.rotation = parseFloat(point.direction || 0);
            truckMarker.setIcon(icon);

            const currentPath = ROUTE_DATA.slice(0, idx + 1).map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }));
            polyline.setPath(currentPath);

            if (isPlaying) {
                map.panTo(pos);
            }
        }

        slider.addEventListener('input', () => {
            currentIdx = parseInt(slider.value);
            currentIdxFloat = currentIdx;
            updateUI(currentIdx);
            if (isPlaying) pausePlay();
        });

        playBtn.addEventListener('click', () => {
             if(isPlaying) pausePlay();
             else startPlay();
        });

        function startPlay() {
            if(currentIdx >= ROUTE_DATA.length - 1) {
                currentIdx = 0;
                currentIdxFloat = 0;
            }
            isPlaying = true;
            playIcon.style.display = 'none';
            pauseIcon.style.display = 'block';
            
            playInterval = setInterval(() => {
                const step = parseFloat(speedSelector.value);
                currentIdxFloat += step;
                currentIdx = Math.floor(currentIdxFloat);
                
                if (currentIdx >= ROUTE_DATA.length - 1) {
                    currentIdx = ROUTE_DATA.length - 1;
                    currentIdxFloat = currentIdx;
                    pausePlay();
                }
                
                slider.value = currentIdx;
                updateUI(currentIdx);
            }, 100);
        }

        function pausePlay() {
            isPlaying = false;
            playIcon.style.display = 'block';
            pauseIcon.style.display = 'none';
            clearInterval(playInterval);
        }

        resetBtn.addEventListener('click', () => {
            currentIdx = 0;
            currentIdxFloat = 0;
            slider.value = 0;
            updateUI(0);
            pausePlay();
        });

        trafficToggle.addEventListener('click', () => {
            if (trafficLayer.getMap()) {
                trafficLayer.setMap(null);
                trafficToggle.classList.remove('active');
                trafficToggle.innerText = 'Show Traffic';
            } else {
                trafficLayer.setMap(map);
                trafficToggle.classList.add('active');
                trafficToggle.innerText = 'Hide Traffic';
            }
        });

        window.onload = initMap;
    </script>
</body>
</html>`;

  const blob = new Blob([htmlTemplate], { type: "text/html;charset=utf-8" });
  FileSaver.saveAs(blob, fileName + ".html");
};

export default exportToHTML;
